import { Droplets, Flame, Sparkles, Zap } from "lucide-react";

export interface ScreedSystem {
  id: string;
  slug: string;
  name: string;
  tagline: string;
  icon: React.ElementType;
  category: string;
  shortDescription: string;
  description: string;
  longDescription: string[];
  bestFor: string[];
  specs: { label: string; value: string }[];
  highlights: string[];
  sectors: { title: string; description: string }[];
  faqs: { question: string; answer: string }[];
  metaTitle: string;
  metaDescription: string;
  keywords: string[];
}

export const screedSystems: ScreedSystem[] = [
  {
    id: "cemfloor-therm",
    slug: "underfloor-heating-screed",
    name: "Underfloor Heating Screed",
    tagline: "Cemfloor Therm Screed",
    icon: Flame,
    category: "For Underfloor Heating",
    shortDescription: "Screed made for underfloor heating",
    description:
      "Cemfloor Therm is made specially to go over underfloor heating pipes. It wraps around them with no air gaps, so heat travels through your floor quickly, warming your rooms faster and cutting your energy bills.",
    longDescription: [
      "Cemfloor Therm is a high-quality liquid screed made for underfloor heating systems in homes and businesses across Exeter, Devon and the South West.",
      "Because it transfers heat so well, your rooms warm up noticeably faster than with traditional screed, which means your heating doesn't have to run as long, saving you money.",
      "We pump it straight over your heating pipes, where it settles flat to within 2mm using laser guides. You can walk on it in 24 to 48 hours, and it won't shrink or crack.",
    ],
    bestFor: [
      "Underfloor heating builds",
      "Residential new builds",
      "Energy-efficient homes",
      "Extensions",
    ],
    specs: [
      { label: "Min Depth", value: "35mm over pipe" },
      { label: "Walk-on Time", value: "24 to 48 hrs" },
      { label: "Thermal Cond.", value: "Up to 2.9 W/m.K" },
      { label: "Guarantee", value: "10 years (Cemfloor)" },
    ],
    highlights: [
      "Transfers heat faster than traditional screed, warmer rooms, lower bills",
      "Wraps completely around heating pipes with no air gaps",
      "Laser-levelled to within 2mm for a perfectly smooth floor",
      "Walk on time is 24 to 48 hours",
    ],
    sectors: [
      {
        title: "New Build Homes",
        description:
          "The top choice for low-energy timber frame and masonry homes across Devon.",
      },
      {
        title: "Home Extensions",
        description:
          "Poured quickly at low depths over UFH systems in rear or kitchen extensions.",
      },
      {
        title: "Heat Pump Projects",
        description:
          "Great heat transfer for heat pump and biomass underfloor heating setups.",
      },
      {
        title: "Commercial Builds",
        description:
          "Fast heat response keeps energy bills down in offices and commercial spaces.",
      },
    ],
    faqs: [
      {
        question: "Why is Cemfloor Therm better for underfloor heating?",
        answer:
          "Cemfloor Therm transfers heat much better than traditional screed. This means warmth reaches your room faster, so your boiler or heat pump doesn't need to run as long, saving you money on energy bills.",
      },
      {
        question: "How thick does Cemfloor Therm need to be over pipes?",
        answer:
          "It only needs 25mm to 35mm of cover over the top of your underfloor heating pipes. This means a thinner floor build-up and quicker room warm-up.",
      },
      {
        question: "When can I turn the underfloor heating on?",
        answer:
          "You can start running your underfloor heating after about 7 days, following the manufacturer's guidelines.",
      },
    ],
    metaTitle: "Underfloor Heating Screed Exeter | Cemfloor Therm UFH",
    metaDescription:
      "Maximize underfloor heating efficiency in Exeter & Devon with Cemfloor Therm liquid screed. Fast heat transfer & quick drying times. Get a free quote!",
    keywords: [
      "underfloor heating screed Exeter",
      "Cemfloor Therm Devon",
      "UFH screed Exeter",
      "liquid screed for underfloor heating Devon",
    ],
  },
  {
    id: "cemfloor-c30",
    slug: "heavy-duty-screed",
    name: "Heavy-Duty High-Strength Screed",
    tagline: "Cemfloor C30 Screed",
    icon: Droplets,
    category: "For Heavy-Use Floors",
    shortDescription: "Strong screed for garages and busy floors",
    description:
      "Cemfloor C30 is a tough, strong liquid screed designed for busy areas, commercial floors, and anywhere that needs a hard-wearing surface.",
    longDescription: [
      "Cemfloor C30 is a strong liquid screed that delivers C30 strength, made for demanding floors in homes and businesses across Exeter and Devon.",
      "It levels itself and packs down tightly with no air pockets or trowel marks, giving you a dense, hard-wearing floor that's ready for tiles, wood, vinyl or resin.",
      "Because it's cement-based, it doesn't form a dusty surface layer like some other screeds, which saves you time and money on preparation before your final floor goes down.",
    ],
    bestFor: [
      "Garages & workshops",
      "High traffic areas",
      "Commercial floors",
      "Large developments",
    ],
    specs: [
      { label: "Strength", value: "C30 / F6" },
      { label: "Min Depth", value: "25mm bonded" },
      { label: "Walk-on Time", value: "24 to 48 hrs" },
      { label: "Sanding", value: "Not needed" },
    ],
    highlights: [
      "Strong C30 rating, built to handle heavy use",
      "No dusty layer forms, so no sanding needed before tiling",
      "We can pump up to 1,000m squared in a single day",
      "Great for commercial floors and busy homes",
    ],
    sectors: [
      {
        title: "Commercial Buildings",
        description:
          "Durable C30 strength floor for retail, healthcare, school and office developments.",
      },
      {
        title: "New Build Homes",
        description:
          "Heavy-duty ground floor and upper floor pours for multi-storey residential projects.",
      },
      {
        title: "Busy Corridors",
        description:
          "Resists heavy footfall and rolling loads without cracking or surface wear.",
      },
      {
        title: "Garages & Workshops",
        description:
          "Tough cementitious slab suitable for vehicle traffic and light workshop machinery.",
      },
    ],
    faqs: [
      {
        question: "Does Cemfloor C30 need sanding?",
        answer:
          "No. Unlike some other screeds, Cemfloor C30 doesn't form a weak, dusty layer on top as it dries. This means you can skip the costly sanding step before laying your floor coverings.",
      },
      {
        question: "How strong is Cemfloor C30?",
        answer:
          "It reaches C30 strength (30 N/mm squared), which makes it suitable for heavy-use and commercial floors.",
      },
    ],
    metaTitle: "Garage & Commercial Screed Exeter | Heavy Duty C30",
    metaDescription:
      "High-strength Cemfloor liquid screed for commercial spaces, workshops & garage floors in Exeter & Devon. Durable, level finish. Request a fixed quote!",
    keywords: [
      "heavy duty screed Exeter",
      "Cemfloor C30 Devon",
      "high strength liquid screed Exeter",
      "garage screed Devon",
    ],
  },
  {
    id: "cemfloor-non-sanding",
    slug: "ready-to-tile-screed",
    name: "Ready-To-Tile Screed",
    tagline: "Cemfloor Non-Sanding Screed",
    icon: Sparkles,
    category: "For Tiling & Vinyl Floors",
    shortDescription: "No sanding needed before tiling",
    description:
      "Cemfloor Non-Sanding screed dries to a clean, hard surface with no dusty layer. Your tiler or floor fitter can prime and lay tiles or vinyl straight on top, no sanding required.",
    longDescription: [
      "Cemfloor Non-Sanding screed is designed to remove one of the biggest headaches in floor preparation: sanding off the dusty surface layer.",
      "Standard screeds form a soft, dusty film as they dry that has to be sanded off before adhesives will stick. Cemfloor's cement-based technology gives you a clean, hard surface instead.",
      "This means tilers and floor fitters across Exeter, Torquay and Devon can prime and lay your floor finish directly, saving days on your project timeline and the cost of hiring sanding equipment.",
    ],
    bestFor: [
      "Tiled floors",
      "Fast-track renovations",
      "Luxury vinyl tile (LVT)",
      "Self-build projects",
    ],
    specs: [
      { label: "Prep Required", value: "No sanding" },
      { label: "Walk-on Time", value: "24 to 48 hrs" },
      { label: "Finish", value: "Smooth and flat" },
      { label: "Ready For", value: "Tiles or vinyl" },
    ],
    highlights: [
      "No dusty layer, skips the costly sanding step",
      "Ready for tiling or vinyl directly, just prime and lay",
      "Mix designed to stop cracking and curling",
      "10-year Cemfloor product guarantee on materials",
    ],
    sectors: [
      {
        title: "Tiled Bathrooms & Kitchens",
        description:
          "Direct adhesive application for large-format porcelain and ceramic tiles.",
      },
      {
        title: "Luxury Vinyl Tile (LVT)",
        description:
          "Flat finish ready for quick smoothing coat and glue-down vinyl.",
      },
      {
        title: "Self-Build & Renovations",
        description:
          "Simplifies homebuilding by removing additional sub-contractor sanding fees.",
      },
    ],
    faqs: [
      {
        question: "Why do tilers prefer non-sanding screed?",
        answer:
          "Because standard screed needs sanding to remove the dusty layer before tiles will stick, which creates dust and extra cost. Cemfloor Non-Sanding dries clean and hard, so tilers can prime and tile straight on top.",
      },
      {
        question: "How flat is the finished floor?",
        answer:
          "Every pour is levelled with laser guides to meet the highest industry standard, flat to within 2mm over a 2-metre span.",
      },
    ],
    metaTitle: "Ready-To-Tile Screed Exeter | Non-Sanding Liquid Screed",
    metaDescription:
      "No-sanding liquid screed in Exeter & Devon. Dries clean and hard so tilers can lay tiles or vinyl straight on top. Dust-free with a 10-year Cemfloor product guarantee.",
    keywords: [
      "no sanding screed Exeter",
      "ready to tile screed Devon",
      "Cemfloor non-sanding Exeter",
      "tiling screed Devon",
    ],
  },
  {
    id: "fastdry",
    slug: "fast-drying-screed",
    name: "Rapid-Drying Screed",
    tagline: "Fast-Drying Cemfloor Screed",
    icon: Zap,
    category: "For Tight Deadlines",
    shortDescription: "Dries fast for tight deadlines",
    description:
      "Our fast-drying Cemfloor screed uses special additives to dry much faster than standard screed. This means you can fit your final floor covering sooner, and we always test the moisture level first to be sure it's ready.",
    longDescription: [
      "When your deadline is tight, Fast-Drying Cemfloor Screed dries quickly while keeping full strength and a perfectly flat finish.",
      "Traditional screeds can take 60 to 90 days to dry fully. Our fast-drying Cemfloor can be ready for your floor covering in as little as 10 to 14 days under normal conditions.",
      "We always test the moisture level on site before giving the all-clear, so your tiles, wood or vinyl can be fitted with complete confidence.",
    ],
    bestFor: [
      "Tight deadlines",
      "Commercial renovations",
      "Winter pours",
      "Fast-track shop fit-outs",
    ],
    specs: [
      { label: "Drying Time", value: "From 10 to 14 days" },
      { label: "Moisture Test", value: "Included" },
      { label: "Walk-on Time", value: "24 hrs" },
      { label: "Strength", value: "High strength" },
    ],
    highlights: [
      "Dries up to 60% faster than standard screed",
      "We test the moisture level on site before you fit your floor",
      "Great for tight deadlines and renovation projects",
      "Keeps full strength and a perfectly flat finish",
    ],
    sectors: [
      {
        title: "Shops & Restaurants",
        description:
          "Fast-track fit-outs where every day of delay costs business revenue.",
      },
      {
        title: "Winter Building",
        description:
          "Speeds up moisture evaporation even during cold, humid Devon winter months.",
      },
      {
        title: "Home Renovations",
        description:
          "Minimises disruption for homeowners waiting to move back into extended living spaces.",
      },
    ],
    faqs: [
      {
        question: "How soon can I fit floor coverings on fast-drying Cemfloor?",
        answer:
          "Depending on the depth and room conditions, you can often fit your floor covering in 10 to 14 days after the pour.",
      },
      {
        question: "Do you check the moisture level?",
        answer:
          "Yes, we test the moisture level on site using proper testing equipment to make sure it's safe to lay your flooring.",
      },
    ],
    metaTitle: "Fast-Drying Liquid Screed Exeter | Walk-On in 24 Hours",
    metaDescription:
      "Keep your project moving with rapid-curing Cemfloor liquid screed in Exeter & Devon. Ready for foot traffic in 24-48 hours. Request a free quote today!",
    keywords: [
      "fast drying screed Exeter",
      "rapid drying screed Devon",
      "quick dry liquid screed Exeter",
      "accelerated screed Devon",
    ],
  },
];

export const getScreedSystem = (slug: string) =>
  screedSystems.find((s) => s.slug === slug);
